/**
 * Free Gift with Purchase — Alpine.js Store
 *
 * Manages free gift eligibility based on cart total,
 * integrates with the mini-cart upsell slider,
 * handles duplicate prevention, and auto-removes
 * gifts when the cart drops below the threshold.
 *
 * Configuration is rendered server-side by
 * snippets/free-gift-config.liquid as a JSON <script> tag.
 */
(function () {
  'use strict';

  /* ───────────────────────────────────────────
   * 1. Parse server-rendered config
   * ─────────────────────────────────────────── */
  var configEl = document.getElementById('free-gift-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[FreeGift] Invalid config JSON:', e);
    return;
  }

  if (!config.enabled || !config.products || !config.products.length) return;

  /* ───────────────────────────────────────────
   * 2. Resolve $0 "Gift" variant for each product
   * ─────────────────────────────────────────── */
  config.products.forEach(function (product) {
    var giftVariant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].price === 0 && product.variants[i].available) {
        giftVariant = product.variants[i];
        break;
      }
    }
    product.giftVariantId = giftVariant ? giftVariant.id : null;

    // Point handleVariant.selectedVariant at the $0 variant
    // so the Add-to-Cart button adds the correct one
    if (giftVariant && product.handleVariant) {
      product.handleVariant.selectedVariant = giftVariant;
    }
  });

  var validProducts = config.products.filter(function (p) {
    return p.giftVariantId !== null;
  });

  var hasGiftVariants = validProducts.length > 0;

  if (!hasGiftVariants) {
    console.info('[FreeGift] No $0 variants found — store will init for threshold messaging only.');
    validProducts = config.products;
  }

  var giftVariantIds = validProducts
    .filter(function (p) { return p.giftVariantId !== null; })
    .map(function (p) { return p.giftVariantId; });

  // Guard flag — prevents recursive cart-update loops
  var isManagingCart = false;

  /* ───────────────────────────────────────────
   * 3. Alpine Store
   * ─────────────────────────────────────────── */
  document.addEventListener('alpine:init', function () {
    Alpine.store('freeGift', {
      /* ── state ── */
      config: config,
      products: validProducts,
      giftVariantIds: giftVariantIds,
      qualifies: false,
      giftInCart: false,
      checking: false,
      remaining: config.threshold,
      progressPct: 0,

      /* ── helpers ── */

      /** Is this variant ID one of our gift variants? */
      isGiftVariant: function (variantId) {
        return giftVariantIds.indexOf(parseInt(variantId)) !== -1;
      },

      /** Cart subtotal excluding gift line items (in cents) */
      getNonGiftTotal: function (cartItems) {
        var total = 0;
        for (var i = 0; i < cartItems.length; i++) {
          if (!this.isGiftVariant(cartItems[i].variant_id)) {
            total += cartItems[i].final_line_price;
          }
        }
        return total;
      },

      /** Number of non-gift line items */
      getNonGiftItemCount: function (cartItems) {
        var count = 0;
        for (var i = 0; i < cartItems.length; i++) {
          if (!this.isGiftVariant(cartItems[i].variant_id)) count++;
        }
        return count;
      },

      /* ── qualification ── */

      /** Re-evaluate whether the cart meets the threshold */
      checkQualification: function (cartItems) {
        if (!cartItems || !cartItems.length) {
          this.qualifies = false;
          this.giftInCart = false;
          this.remaining = config.threshold;
          this.progressPct = 0;
          return;
        }

        var nonGiftTotal = this.getNonGiftTotal(cartItems);
        var nonGiftCount = this.getNonGiftItemCount(cartItems);

        // threshold === 0 means "free with any purchase"
        this.qualifies = config.threshold === 0
          ? nonGiftCount > 0
          : nonGiftTotal >= config.threshold;

        // Progress bar: how much remains and percentage complete
        if (config.threshold > 0) {
          var diff = config.threshold - nonGiftTotal;
          this.remaining = diff > 0 ? diff : 0;
          this.progressPct = Math.min((nonGiftTotal / config.threshold) * 100, 100);
        } else {
          this.remaining = 0;
          this.progressPct = nonGiftCount > 0 ? 100 : 0;
        }

        this.giftInCart = false;
        for (var i = 0; i < cartItems.length; i++) {
          if (this.isGiftVariant(cartItems[i].variant_id)) {
            this.giftInCart = true;
            break;
          }
        }
      },

      /* ── cart operations ── */

      /** Add a gift variant to the cart */
      addGiftToCart: function (variantId) {
        if (this.giftInCart) return Promise.resolve();
        var self = this;
        isManagingCart = true;

        return fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
        })
          .then(function () {
            self.giftInCart = true;
            return fetch(window.Shopify.routes.root + 'cart.js', {
              headers: { 'Content-Type': 'application/json' }
            }).then(function (r) { return r.json(); });
          })
          .then(function (cartData) {
            self.checkQualification(cartData.items);
            window.dispatchEvent(new CustomEvent('cartupdated', { detail: { cart: cartData } }));
            self.injectIntoUpsellSlider();
            self.sortGiftItemsInCart();
          })
          .catch(function (err) {
            console.error('[FreeGift] Error adding gift:', err);
          })
          .finally(function () {
            setTimeout(function () { isManagingCart = false; }, 2000);
          });
      },

      /** Remove all gift line items from the cart */
      removeGiftsFromCart: function (cartItems) {
        var self = this;
        var giftItems = [];
        for (var i = 0; i < cartItems.length; i++) {
          if (this.isGiftVariant(cartItems[i].variant_id)) {
            giftItems.push(cartItems[i]);
          }
        }
        if (!giftItems.length) return Promise.resolve();

        isManagingCart = true;

        var updates = {};
        giftItems.forEach(function (item) {
          updates[item.key] = 0;
        });

        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) { return r.json(); })
          .then(function (cartData) {
            self.giftInCart = false;
            self.showRemovedNotification();
            self.checkQualification(cartData.items);
            window.dispatchEvent(new CustomEvent('cartupdated', { detail: { cart: cartData } }));
            self.injectIntoUpsellSlider();
          })
          .catch(function (err) {
            console.error('[FreeGift] Error removing gifts:', err);
          })
          .finally(function () {
            setTimeout(function () { isManagingCart = false; }, 2000);
          });
      },

      /* ── main check (called on every cart update) ── */

      checkAndManageGift: function () {
        if (this.checking || isManagingCart) return Promise.resolve();
        this.checking = true;
        var self = this;

        return fetch(window.Shopify.routes.root + 'cart.js', {
          headers: { 'Content-Type': 'application/json' }
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            self.checkQualification(cart.items);

            // Auto-remove gift when no longer qualified
            if (!self.qualifies && self.giftInCart) {
              return self.removeGiftsFromCart(cart.items);
            }
          })
          .catch(function (err) {
            console.error('[FreeGift] Error checking cart:', err);
          })
          .finally(function () {
            self.checking = false;
          });
      },

      /* ── mini-cart upsell slider integration ── */

      /** Push gift products into handleUpsells.activeUpsells */
      injectIntoUpsellSlider: function () {
        var miniCartContent = document.querySelector('.mini-cart_content');
        if (!miniCartContent) return;

        var data;
        try {
          data = Alpine.$data(miniCartContent);
        } catch (e) { return; }
        if (!data || !data.activeUpsells) return;

        var self = this;
        this.products.forEach(function (gift) {
          var alreadyInSlider = false;
          for (var i = 0; i < data.activeUpsells.length; i++) {
            if (data.activeUpsells[i].id === gift.giftVariantId) {
              data.activeUpsells[i].locked = !self.qualifies || self.giftInCart;
              alreadyInSlider = true;
              break;
            }
          }
          if (!alreadyInSlider && !self.giftInCart) {
            data.activeUpsells.push({
              id: gift.giftVariantId,
              title: gift.title,
              price: 0,
              compareAtPrice: gift.price,
              imageSrc: gift.imageSrc,
              imageAlt: gift.imageAlt,
              available: true,
              url: gift.url,
              isGift: true,
              locked: !self.qualifies,
              handleVariant: gift.handleVariant
            });
          }
        });
      },

      /* ── cart line-item ordering ── */

      /** Move gift line items to the end of the mini-cart list */
      sortGiftItemsInCart: function () {
        var miniCart = document.querySelector('[x-data*="handleMinicart"]');
        if (!miniCart) return;

        var data;
        try { data = Alpine.$data(miniCart); } catch (e) { return; }
        if (!data || !data.cart || !data.cart.items || data.cart.items.length < 2) return;

        var self = this;
        var nonGifts = [];
        var gifts = [];
        for (var i = 0; i < data.cart.items.length; i++) {
          if (self.isGiftVariant(data.cart.items[i].variant_id)) {
            gifts.push(data.cart.items[i]);
          } else {
            nonGifts.push(data.cart.items[i]);
          }
        }

        if (gifts.length && nonGifts.length) {
          data.cart.items = nonGifts.concat(gifts);
        }
      },

      /* ── notification ── */

      showRemovedNotification: function () {
        // Dispatch event that the mini-cart message handler can pick up
        window.dispatchEvent(new CustomEvent('showcartmessage', {
          detail: {
            status: 'info',
            message: config.removedMessage
          }
        }));
      },

      /* ── formatting helpers ── */

      formatMoney: function (cents) {
        if (typeof LiquifyHelper !== 'undefined' && LiquifyHelper.moneyFormat) {
          return LiquifyHelper.moneyFormat(cents, true);
        }
        return '$' + (cents / 100).toFixed(2);
      }
    });
  });

  /* ───────────────────────────────────────────
   * 4. Event listeners
   * ─────────────────────────────────────────── */

  window.addEventListener('cartupdated', function (e) {
    if (isManagingCart) return;
    var store = Alpine.store('freeGift');
    if (!store) return;

    var detail = e.detail || {};
    var cartData = detail.cart || null;

    if (cartData && cartData.items) {
      store.checkQualification(cartData.items);

      if (!store.qualifies && store.giftInCart) {
        store.removeGiftsFromCart(cartData.items).then(function () {
          store.injectIntoUpsellSlider();
          store.sortGiftItemsInCart();
        });
      } else {
        store.injectIntoUpsellSlider();
        store.sortGiftItemsInCart();
      }
    } else {
      store.checkAndManageGift().then(function () {
        store.injectIntoUpsellSlider();
        store.sortGiftItemsInCart();
      });
    }
  });

  // Initial check on page load
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(function () {
      var store = Alpine.store('freeGift');
      if (store) {
        store.checkAndManageGift().then(function () {
          store.injectIntoUpsellSlider();
          store.sortGiftItemsInCart();
        });
      }
    }, 2500);
  });
})();
